import * as pdfjs from 'pdfjs-dist';
import workerSrc from 'pdfjs-dist/build/pdf.worker.mjs?url';
import type { CatalogMetadata, CatalogPageAsset, GenerationProgress } from '../types';
import { randomToken, slugify } from './slug';
import { localFilePath, publicFileUrl } from './siteUrl';

pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

function canvasToWebp(canvas: HTMLCanvasElement, quality: number) {
  return new Promise<string>((resolve) => {
    resolve(canvas.toDataURL('image/webp', quality));
  });
}

async function renderPage(page: pdfjs.PDFPageProxy, pageNumber: number): Promise<CatalogPageAsset> {
  const viewport = page.getViewport({ scale: 1 });
  const fullScale = Math.min(4.5, 3000 / viewport.width);
  const fullViewport = page.getViewport({ scale: fullScale });
  const fullCanvas = document.createElement('canvas');
  const fullContext = fullCanvas.getContext('2d');

  if (!fullContext) {
    throw new Error('Canvas non disponibile nel browser.');
  }

  fullCanvas.width = Math.ceil(fullViewport.width);
  fullCanvas.height = Math.ceil(fullViewport.height);
  fullContext.imageSmoothingEnabled = true;
  fullContext.imageSmoothingQuality = 'high';
  await page.render({ canvasContext: fullContext, viewport: fullViewport }).promise;

  const previewCanvas = document.createElement('canvas');
  const previewContext = previewCanvas.getContext('2d');
  if (!previewContext) {
    throw new Error('Canvas preview non disponibile nel browser.');
  }
  previewContext.imageSmoothingEnabled = true;
  previewContext.imageSmoothingQuality = 'high';
  const previewWidth = 560;
  const ratio = previewWidth / fullCanvas.width;
  previewCanvas.width = previewWidth;
  previewCanvas.height = Math.round(fullCanvas.height * ratio);
  previewContext.drawImage(fullCanvas, 0, 0, previewCanvas.width, previewCanvas.height);

  return {
    page: pageNumber,
    previewUrl: await canvasToWebp(previewCanvas, 0.78),
    fullUrl: await canvasToWebp(fullCanvas, 0.96),
    width: fullCanvas.width,
    height: fullCanvas.height
  };
}

export async function generateCatalogFromPdf(
  file: File,
  title: string,
  onProgress: (progress: GenerationProgress) => void
): Promise<CatalogMetadata> {
  const bytes = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: bytes }).promise;
  const baseSlug = slugify(title || file.name.replace(/\.pdf$/i, '')) || randomToken(10);
  const token = randomToken();
  const created = new Date().toISOString();
  const publicUrl = publicFileUrl(baseSlug) || localFilePath(baseSlug);
  const pages: CatalogPageAsset[] = [];

  onProgress({ phase: 'Analisi PDF', current: 0, total: pdf.numPages });
  for (let index = 1; index <= pdf.numPages; index += 1) {
    onProgress({ phase: `Rendering pagina ${index}`, current: index - 1, total: pdf.numPages });
    const page = await pdf.getPage(index);
    pages.push(await renderPage(page, index));
    onProgress({ phase: `Pagina ${index} pronta`, current: index, total: pdf.numPages });
  }

  const pdfUrl = URL.createObjectURL(file);
  onProgress({ phase: 'Pronto', current: pdf.numPages, total: pdf.numPages });

  return {
    title: title || file.name.replace(/\.pdf$/i, ''),
    slug: baseSlug,
    token,
    created,
    updated: created,
    pageCount: pdf.numPages,
    publicUrl,
    pdfUrl,
    sourcePdfDataUrl: undefined,
    status: 'ready',
    pages
  };
}
